// @ts-nocheck
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, Tooltip, Cell, BarChart, Bar, LabelList,
  XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  DefinitionTooltip,
} from "src/components/common";
import {
  getOrganicContentSectionDefinitionInfo,
} from "../OrganicMediaDefinitions";
import OrganicSelectMenu from "../OrganicSelectMenu";
import { formatNumber } from "src/utils/numberFormat";
import {
  HorizontalBarTooltip,
  ContentTypeAxisTick,
  formatBarMetricTick,
  type MetricKey,
  type PercentAxisConfig,
} from "./FacebookContentHelpers";
import "../../../styles/FacebookContentReport/FacebookContentPerformanceByType.scss";

type RightChartRow = {
  type: string;
  value: number;
  valueFormattedValue?: string;
  color: string;
};

const FacebookContentPerformanceByType: FC<{
  selectedMetric: MetricKey;
  setSelectedMetric: (_v: MetricKey) => void;
  pagedRightChartData: RightChartRow[];
  rightChartData: RightChartRow[];
  rightChartAxisConfig: PercentAxisConfig;
  contentTypeBarPage: number;
  setContentTypeBarPage: React.Dispatch<React.SetStateAction<number>>;
  contentTypeBarTotalPages: number;
  isRTL: boolean;
}> = ({
  selectedMetric, setSelectedMetric, pagedRightChartData, rightChartData,
  rightChartAxisConfig, contentTypeBarPage, setContentTypeBarPage,
  contentTypeBarTotalPages, isRTL,
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="organic-content-panel organic-content-panel--bar">
      <div className="organic-content-panel__header">
        <div className="organic-content-panel__title">
          <h3>{t("organicMedia.facebook.sections.contentPerformanceByType")}</h3>
          <DefinitionTooltip
            label={t("organicMedia.facebook.sections.contentPerformanceByType")}
            info={getOrganicContentSectionDefinitionInfo("contentPerformanceByType", t)}
            formulaTitle={t("organicMedia.common.formula")}
          />
        </div>
        <div className="organic-bars__select-wrap">
          <OrganicSelectMenu
            className="organic-bars__select"
            ariaLabel={t("organicMedia.contentReport.engagementMetricSelectAria")}
            value={selectedMetric}
            onChange={value => setSelectedMetric(value as MetricKey)}
            options={[
              { value: "engagementRate", label: t("organicMedia.facebook.metrics.engagementRate") },
              { value: "organicReach", label: t("organicMedia.facebook.metrics.organicReach") },
              { value: "ctr", label: t("organicMedia.facebook.metrics.ctr") },
              { value: "impressions", label: t("organicMedia.facebook.metrics.impressions") },
              { value: "clicks", label: t("organicMedia.facebook.metrics.clicks") },
            ]}
          />
        </div>
      </div>
      <div className="organic-content-panel__chart-fill">
      <ResponsiveContainer width="100%" height={340}>
        <BarChart
          data={pagedRightChartData}
          margin={
            isRTL
              ? { top: 24, right: 12, left: 6, bottom: 12 }
              : { top: 24, right: 6, left: 12, bottom: 12 }
          }
        >
          <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="type"
            reversed={isRTL}
            tick={<ContentTypeAxisTick />}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            tickMargin={14}
            interval={0}
            height={20}
          />
          <YAxis
            orientation={isRTL ? "right" : "left"}
            width={70}
            tickMargin={isRTL ? 10 : 4}
            tick={(tickProps: { x?: number; y?: number; payload?: { value: number } }) => {
              const { x = 0, y = 0, payload } = tickProps;
              return (
                <text
                  x={x}
                  y={y}
                  dy={4}
                  // Recharts anchors a right-side axis tick with `start` (text grows
                  // away from the plot, to the right) — but SVG `text-anchor` reads
                  // its start/end edge off the inherited `direction`, and this text
                  // sits inside an Arabic `dir="rtl"` page, which flips it and sends
                  // the label growing back over the axis line instead. Pin `direction`
                  // to ltr so the anchor means what Recharts intended regardless.
                  textAnchor={isRTL ? "start" : "end"}
                  direction="ltr"
                  fill="#5e6a7d"
                  fontSize={13}
                >
                  {formatBarMetricTick(selectedMetric, payload?.value ?? 0, i18n.language)}
                </text>
              );
            }}
            axisLine={{ stroke: "#9aa5b8" }}
            tickLine={{ stroke: "#9aa5b8" }}
            domain={rightChartAxisConfig.domain}
            ticks={rightChartAxisConfig.ticks}
          />
          <Tooltip
            content={<HorizontalBarTooltip metricKey={selectedMetric} />}
            cursor={{ fill: "rgba(109, 40, 217, 0.08)" }}
            allowEscapeViewBox={{ x: false, y: true }}
            wrapperStyle={{ pointerEvents: "none" }}
          />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={36}>
            <LabelList
              dataKey="value"
              position="top"
              dy={-8}
              formatter={(value: number | string) => formatBarMetricTick(selectedMetric, value, i18n.language)}
              className="organic-bars__value-label"
            />
            {pagedRightChartData.map(entry => (
              <Cell key={`${entry.type}-${selectedMetric}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
    {rightChartData.length > 0 ? (
      <div className="organic-bars__pagination">
        <button
          type="button"
          onClick={() => setContentTypeBarPage(page => Math.max(1, page - 1))}
          disabled={contentTypeBarPage === 1}
          aria-label={t("organicMedia.common.previousPage")}
        >
          {isRTL ? "›" : "‹"}
        </button>
        <span>
          {t("pagination.pageOf", {
            current: formatNumber(contentTypeBarPage, {}, i18n.language),
            total: formatNumber(contentTypeBarTotalPages, {}, i18n.language),
          })}
        </span>
        <button
          type="button"
          onClick={() =>
            setContentTypeBarPage(page =>
              Math.min(contentTypeBarTotalPages, page + 1)
            )
          }
          disabled={contentTypeBarPage === contentTypeBarTotalPages}
          aria-label={t("organicMedia.common.nextPage")}
        >
          {isRTL ? "‹" : "›"}
        </button>
      </div>
    ) : null}
    </div>
  );
};

export default FacebookContentPerformanceByType;
